import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "./AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const DEMO = [
  { label: "Admin", email: "ai.jaineel@gmail.com", password: "Admin@123" },
  { label: "Sales Staff", email: "rohan.sales@megatronixvision.com", password: "Staff@123" },
  { label: "Telecaller", email: "nikhil.tele@megatronixvision.com", password: "Staff@123" },
  { label: "Accountant", email: "accounts@megatronixvision.com", password: "Staff@123" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(form.email.trim(), form.password, form.remember);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Unable to sign in."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Enter your work email and password to open your workspace."
      footer={
        <>
          New here?{" "}
          <Link to="/signup" className="font-semibold text-primary hover:underline">Create an account</Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email" type="email" required autoComplete="email" data-testid="login-email"
            placeholder="you@company.com" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="h-11"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline">Forgot password?</Link>
          </div>
          <div className="relative">
            <Input
              id="password" type={show ? "text" : "password"} required autoComplete="current-password"
              data-testid="login-password" placeholder="Enter your password" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="h-11 pr-10"
            />
            <button type="button" onClick={() => setShow(!show)} aria-label="Toggle password"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2.5 text-sm text-muted-foreground">
          <Checkbox checked={form.remember} onCheckedChange={(v) => setForm({ ...form, remember: !!v })} data-testid="login-remember" />
          Keep me signed in on this device
        </label>

        <Button type="submit" size="lg" disabled={busy} className="w-full" data-testid="login-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
          Sign in
        </Button>
      </form>

      <div className="mt-8 rounded-xl border border-border bg-card p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Demo accounts</p>
        <div className="mt-3 grid gap-2">
          {DEMO.map((d) => (
            <button
              key={d.email}
              type="button"
              onClick={() => setForm({ ...form, email: d.email, password: d.password })}
              data-testid={`demo-${d.label.split(" ")[0].toLowerCase()}`}
              className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary-soft/50"
            >
              <span>
                <span className="block text-xs font-semibold">{d.label}</span>
                <span className="block text-[11px] text-muted-foreground">{d.email}</span>
              </span>
              <span className="text-[11px] font-medium text-primary">Use</span>
            </button>
          ))}
        </div>
      </div>
    </AuthLayout>
  );
}
